import { encryptionPhone } from '@flowus/common';
import { PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import type { SetStateAction } from 'jotai';
import type { Dispatch, FC } from 'react';
import { colors } from 'src/colors';
import { Avatar } from 'src/common/components/avatar';
import { Icon } from 'src/common/components/icon';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { useOpenModal } from 'src/common/components/next-modal';
import { Switch } from 'src/common/components/switch';
import { UNNAMED_USER, publicLink } from 'src/common/const';
import { request } from 'src/common/request';
import { SearchUser } from 'src/components/share/search-user';
import { UserAvatar } from 'src/components/user-avatar';
import { PRODUCT_AI_TITLE } from 'src/const/title';
import { useCurrentSpace } from 'src/hooks/space';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useCurrentSpaceUsers } from 'src/hooks/space/use-current-space-users';
import { useSearchSpaceMembers } from 'src/hooks/space/use-search-space-members';
import { useCurrentUser } from 'src/hooks/user';
import { $appUiStateCache, setAppUiState, useAiMembers } from 'src/services/app';
import { bizTracker } from 'src/utils/biz-tracker';
import { FoldItemList } from 'src/views/main/setting-modal/members-setting/fold-item-list';
import { Option } from '../option';
import { SettingProvider } from '../common';
import { useModel } from 'src/common/create-model';

export const FlowUsAISetting: FC = () => {
  const {
    spaceSettingState: { enableAI, setAI },
  } = useModel(SettingProvider);
  const { uuid } = useCurrentUser();
  const users = useCurrentSpaceUsers();
  const currentSpace = useCurrentSpace();
  const openModal = useOpenModal();
  const aiMembers = useAiMembers();
  const searchSpaceMembers = useSearchSpaceMembers();
  const userList = searchSpaceMembers({ keywords: undefined });
  const currentUser = users[uuid];

  const openOption = (
    event: React.MouseEvent,
    userId: string,
    type: 'user' | 'group' | 'space'
  ) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'bottom',
      content: ({ onCloseModal }) => (
        <PermissionSetting uuid={userId} closeSelf={onCloseModal} type={type} />
      ),
    });
  };

  const openSelect = (event: React.MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'left',
      content: ({ onCloseModal }) => (
        <div className="next-modal w-[400px] pt-2.5">
          <SearchUser
            searchType="aiMember"
            uuid={getCurrentSpaceId()}
            backToMainPanel={onCloseModal}
          />
        </div>
      ),
    });
  };

  const isTeam = currentSpace.planType?.toLowerCase()?.includes('team');
  const uuidSets = new Set();
  aiMembers.forEach((item) => uuidSets.add(item.uuid));

  const { permissionGroups = [] } = currentSpace;
  const groups: any[] = [];
  permissionGroups.forEach((group) => {
    if (uuidSets.has(group.id)) {
      groups.push({
        ...group,
        uuid: group.id,
        type: PermissionType.GROUP,
        name: group.name,
        userIds: group.userIds,
      });
    }
  });

  const aiUsers = userList.filter((item) => uuidSets.has(item.uuid));

  return (
    <>
      <Option
        title={PRODUCT_AI_TITLE}
        description={
          !__BUILD_IN__ && (
            <>
              关闭后，空间里将不再显示 AI 辅助入口，
              <a className="underline ml-1" target={'_blank'} href={publicLink.aiTerms}>
                {PRODUCT_AI_TITLE}
                &nbsp;声明
              </a>
            </>
          )
        }
        rightPart={
          !__BUILD_IN__ && (
            <Switch
              open={enableAI}
              onSwitch={(status) => {
                bizTracker.event('ai_switch_set', { is_open: status });
                setAI(status);
              }}
            />
          )
        }
      />

      {isTeam && enableAI && (
        <div className="mt-2.5">
          {uuidSets.has(currentSpace.uuid) && (
            <div className="group flex shrink-0 items-center h-[58px]">
              <Avatar
                className="w-[34px] h-[34px] mr-2"
                name={currentSpace.title}
                icon={currentSpace.icon}
                color={currentSpace.backgroundColor}
                iconSize={34}
                style={{ fontSize: '20px' }}
              />
              <div className="flex flex-col text-t2 grow mr-3 text-ellipsis">
                {currentSpace.title}（{Object.keys(users).length} 人）
              </div>

              <button
                className="flex items-center ml-auto flex-shrink-0"
                onClick={(event) => openOption(event, currentSpace.uuid, 'space')}
              >
                允许
                <Icon name="IcArrow" className="ml-1" size="xxxsmall" />
              </button>
            </div>
          )}

          <FoldItemList
            foldLength={50}
            items={aiUsers}
            renderItemContent={(user) => {
              const userEditorRole = currentUser?.spaceRole === PermissionRole.EDITOR;
              const markName = currentSpace.userRemark?.[user.uuid];
              let userName = user.nickname;
              if (markName) {
                userName = `${markName} (${user.nickname || UNNAMED_USER})`;
              }
              return (
                <div className="group flex shrink-0 items-center h-[58px]" key={user.uuid}>
                  <UserAvatar user={user} className="mr-2 w-[30px] h-[30px] !text-t1" />
                  <div className="flex flex-col grow mr-3 text-ellipsis">
                    <div className="flex items-center">
                      <span className="mb-0.5 text-t2">{userName || UNNAMED_USER}</span>
                    </div>
                    <span className="text-grey3 text-t4">
                      {encryptionPhone(user.phone || user.email)}
                    </span>
                  </div>

                  {userEditorRole && (
                    <button
                      className="flex items-center ml-auto flex-shrink-0"
                      onClick={(event) => openOption(event, user.uuid, 'user')}
                    >
                      允许
                      <Icon name="IcArrow" className="ml-1" size="xxxsmall" />
                    </button>
                  )}
                </div>
              );
            }}
          />

          <FoldItemList
            foldLength={50}
            items={groups}
            renderItemContent={(item) => {
              const userEditorRole = currentUser?.spaceRole === PermissionRole.EDITOR;

              return (
                <div className="group flex shrink-0 items-center h-[58px]" key={item.uuid}>
                  <Avatar
                    className="w-[34px] h-[34px] mr-2 text-white"
                    name={item.name}
                    icon={item.icon}
                    color={colors.grey}
                    iconSize={34}
                    style={{ fontSize: '20px' }}
                  />
                  <div className="flex flex-col text-t2 grow mr-3 text-ellipsis">
                    {item.name}（{item.userIds.length} 人）
                  </div>

                  {userEditorRole && (
                    <button
                      className="flex items-center ml-auto flex-shrink-0"
                      onClick={(event) => openOption(event, item.uuid, 'group')}
                    >
                      允许
                      <Icon name="IcArrow" className="ml-1" size="xxxsmall" />
                    </button>
                  )}
                </div>
              );
            }}
          />

          {currentUser?.spaceRole === PermissionRole.EDITOR && (
            <button
              className="flex flex-shrink-0 items-center pl-2 h-9 text-grey4 text-t3"
              onClick={openSelect}
            >
              <Icon size="middle" name="IcAddBoard" className="mr-1 " />
              <span className="text-t2">添加成员</span>
            </button>
          )}
        </div>
      )}
    </>
  );
};

const PermissionSetting: FC<{
  uuid: string;
  closeSelf: () => void;
  type: 'user' | 'group' | 'space';
}> = ({ type, uuid, closeSelf }) => {
  const items = [
    {
      type: ListItemType.TEXT_DESCRIPTION,
      data: { type: 'allow', title: '允许', onClick: () => closeSelf() },
    },
    { type: ListItemType.LINE },
    {
      type: ListItemType.TEXT_DESCRIPTION,
      data: {
        title: <div className="text-red">移除</div>,
        onClick: async () => {
          closeSelf();

          const spaceId = getCurrentSpaceId();
          await request.infra.deleteSpaceAiMembers(spaceId, {
            type,
            uuid,
          });

          setAppUiState({
            $aiMembers: $appUiStateCache.$aiMembers.filter((item) => item.uuid !== uuid),
          });
        },
      },
    },
  ];

  return <ListView className="rounded-r next-modal py-1.5 w-[120px]" items={items} />;
};
